import { Link } from 'react-router-dom';
import { Shield } from 'lucide-react';
import LegalLayout from '../LegalLayout';

const PrivacyPolicy = () => {
  const accentColor = '#fb7185';
  const lastUpdated = 'July 30, 2026';

  return (
    <LegalLayout app="Nuzzle" docType="Privacy Policy" icon={Shield} lastUpdated={lastUpdated}>
      <div className="space-y-8">
            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Introduction
              </h2>
              <p className="mb-4 leading-relaxed">
                Feral Techlabs ("we," "our," or "us") operates the Nuzzle mobile application (the "App"). Nuzzle keeps your pets’ medication routine organized: track what your vet prescribed, log every dose in a tap, and always know what is done and what is still due today.
              </p>
              <p className="leading-relaxed">
                This Privacy Policy explains how we handle your information when you use the App. We are committed to protecting your privacy and being transparent about our practices.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Our Privacy Commitment
              </h2>
              <p className="mb-4 leading-relaxed">
                Nuzzle is built with privacy as a core principle:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong style={{ color: 'var(--text-primary)' }}>Local-First Storage:</strong> Pet profiles, medications, dose logs, and symptoms are stored locally on your device.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>No User Accounts:</strong> The App does not require registration, login, or any form of account creation.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>No Analytics:</strong> We do not collect usage statistics, behavioral data, or any form of telemetry.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>No Third-Party Tracking:</strong> We do not integrate any third-party analytics, tracking, or advertising SDKs.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Information We Store
              </h2>
              <h3 className="text-lg font-medium mb-3 mt-6" style={{ color: 'var(--text-primary)' }}>
                Pet &amp; Medication Data
              </h3>
              <p className="mb-4 leading-relaxed">
                Nuzzle stores the care records you enter, including:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Pet profiles: name, species, breed, age, weight, allergies, and condition notes</li>
                <li>Medications and supplements with dosage, frequency, start and end dates, prescribing vet, and pharmacy</li>
                <li>Dose logs (given, skipped, missed) with timestamps</li>
                <li>Symptom observations with severity, and weight entries over time</li>
              </ul>
              <p className="leading-relaxed">
                Important: All pet health data is stored locally on your device and is never transmitted to any external server. Vet report exports leave the device only when you explicitly share them.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Data Storage &amp; Security
              </h2>
              <p className="mb-4 leading-relaxed">
                Your data is protected by multiple layers of security:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong style={{ color: 'var(--text-primary)' }}>Local Storage:</strong> All data is stored in the App's private on-device storage, protected by iOS's built-in encryption.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>No Remote Servers:</strong> Your pet profiles, medication schedules, and dose history are never uploaded to servers owned or operated by us.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>iOS Sandbox:</strong> The App operates within iOS's security sandbox, preventing other apps from accessing your data.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                What We Do NOT Collect
              </h2>
              <p className="mb-4 leading-relaxed">
                Nuzzle does not collect, store, or transmit:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Personal identification information</li>
                <li>Your vet’s or pharmacy’s data beyond the names you type</li>
                <li>Your device location</li>
                <li>Contact information</li>
                <li>Photos or media files</li>
                <li>Browsing history</li>
                <li>Device identifiers for tracking purposes</li>
                <li>Usage analytics or behavioral data</li>
                <li>Advertising data</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Network Connections
              </h2>
              <p className="leading-relaxed">
                Nuzzle makes no network requests. The App has no backend, calls no external APIs, and works entirely offline. Nothing you enter ever leaves your device unless you explicitly export or share it yourself.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Notifications
              </h2>
              <p className="leading-relaxed">
                If you grant notification permission, Nuzzle schedules local reminders for upcoming doses and refill warnings. All notifications are scheduled on-device through iOS; we never send push notifications from servers.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Subscriptions &amp; In-App Purchases
              </h2>
              <p className="mb-4 leading-relaxed">
                Nuzzle offers an optional Nuzzle Pro subscription (monthly or yearly). All purchases are processed entirely by the Apple App Store. We do not process, store, or have access to your payment information.
              </p>
              <p className="leading-relaxed">
                Entitlement is verified through Apple's StoreKit framework on your device. No purchase or billing data is ever sent to servers of ours — we do not operate any.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Third-Party Services
              </h2>
              <p className="leading-relaxed">
                Nuzzle uses Apple's native frameworks only: SwiftUI, UserNotifications, and StoreKit. We do not integrate any third-party analytics tools, crash reporters, or advertising networks.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Your Rights &amp; Data Control
              </h2>
              <p className="mb-4 leading-relaxed">
                You have complete control over your data:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong style={{ color: 'var(--text-primary)' }}>View &amp; Edit:</strong> Review and edit every pet profile, medication, and log entry directly in the App.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>Delete:</strong> Remove pets, medications, or log entries at any time.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>Revoke Permissions:</strong> Turn off notification permission at any time in iOS Settings; the App will continue to work without reminders.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>Uninstall:</strong> Removing the App deletes all locally stored data permanently.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Children's Privacy
              </h2>
              <p className="leading-relaxed">
                Nuzzle is not directed at children under the age of 13. We do not knowingly collect personal information from children. If you believe a child has provided us with personal information, please contact us so we can take appropriate action.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Changes to This Policy
              </h2>
              <p className="leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Contact Us
              </h2>
              <p className="leading-relaxed">
                If you have any questions about this Privacy Policy or our privacy practices, please contact us:
              </p>
              <div className="mt-4 space-y-2">
                <p>
                  <strong style={{ color: 'var(--text-primary)' }}>Email:</strong>{' '}
                  <a
                    href="mailto:support@feraltechlabs.co.in"
                    className="transition-colors"
                    style={{ color: accentColor }}
                  >
                    support@feraltechlabs.co.in
                  </a>
                </p>
                <p>
                  <strong style={{ color: 'var(--text-primary)' }}>Support:</strong>{' '}
                  <Link
                    to="/apps/nuzzle/support"
                    className="transition-colors"
                    style={{ color: accentColor }}
                  >
                    Nuzzle Support Page
                  </Link>
                </p>
              </div>
            </section>
      </div>
    </LegalLayout>
  );
};

export default PrivacyPolicy;
